import { Element } from '@src/editor/models';
import { v4 as uuid } from 'uuid';

import { VideoElement } from '../models/elements/VideoElement';
import { AbstractCommand } from './AbstractCommand';
import { AddLayoutElementsCommand } from './AddLayoutElementsCommand';
import { SplitLayoutElementsCommand } from './SplitLayoutElementsCommand';
import { TimelineMode } from './TimelineMode';

import type { TrackManager } from '../lib/TrackManager';
import type { Track } from '../models/Track';
import type { VideoEditor } from '../VideoEditor';
import type { Video } from '@src/types.ts/story';
import type { TranscriptTextElement } from '@src/types.ts/transcript';

/**
 * TranscriptSimpleInsertCommand – inserts a contiguous range of transcript
 * words (specified by their original `initial_index` values) back onto the
 * timeline at a given time position.  If the insertion point falls inside an
 * existing VideoElement, that element is split and the right-hand side is
 * rippled to make room.
 *
 * The command stores a memento of the entire transcript track before the
 * operation so that undo can be implemented by restoring that snapshot.  This
 * is slightly heavy-handed but dramatically simplifies the logic for complex
 * ripple / split reversals.
 */
export class TranscriptSimpleInsertCommand extends AbstractCommand {
  private readonly insertTime: number;

  getName(): string {
    return 'TranscriptSimpleInsertCommand';
  }
  private readonly startInitialIdx: number;
  private readonly endInitialIdx: number;

  private transcriptTrack: Track | null = null;
  private readonly trackManager: TrackManager;

  // Track state snapshot for undo (memento pattern)
  private originalTrackState: {
    id: string;
    state: Record<string, any>;
  }[] = [];

  private extraElementDataSnapshot: Video['extraElementData'] | null = null;

  constructor(
    editor: VideoEditor,
    insertTime: number,
    startInitialIdx: number,
    endInitialIdx: number,
  ) {
    super(editor);

    this.insertTime = insertTime;
    this.startInitialIdx = Math.min(startInitialIdx, endInitialIdx);
    this.endInitialIdx = Math.max(startInitialIdx, endInitialIdx);

    this.trackManager = this.editor.getTrackManager();

    const track = this.getTranscriptTrack();
    if (track) {
      this.originalTrackState = track.elements.map(el => ({
        id: el.id,
        state: el.getState(),
      }));
    }
  }

  // ---------------------------------------------------------------------
  // Command lifecycle
  // ---------------------------------------------------------------------

  execute(): boolean {
    const track = this.getTranscriptTrack();
    if (!track) {
      return false;
    }

    const words = this.collectWords();
    if (!words.length) {
      return false;
    }

    // Basic timing data for the clip we are about to create
    const firstWord = words[0];
    const lastWord = words[words.length - 1];
    const mediaTrimStart = firstWord.ts;
    const duration = lastWord.end_ts - mediaTrimStart;

    if (duration <= 0) {
      return false;
    }

    // If an element already exists at this time we need to split it
    const existing = track.findElementAtTime(this.insertTime);
    if (existing) {
      // Split the element at insertTime
      if (this.extraElementData.isLayoutElement(existing)) {
        const command = new SplitLayoutElementsCommand(this.editor, existing, {
          splitTime: this.insertTime,
        });
        this.editor.executeCommand(command);
      } else {
        this.trackManager.splitElement(existing, this.insertTime);
      }
    }

    // Create a new element based on the first element on the track (or blank)
    const template = Element.isVideoElement(track.elements[0])
      ? track.elements[0]
      : null;

    const newElement = template ? template.clone() : new VideoElement();

    newElement.update({
      time: this.insertTime,
      trim_start: mediaTrimStart,
      duration,
    });

    const newId = `inserted-${uuid()}`;
    newElement.id = newId;

    // Set original transcription to initialize the transcript segment
    const originalTranscript =
      this.editor.legacyTranscript?.getOriginalTranscription() || [];

    // newElement.setOriginalTranscription(Array.from(originalTranscript));

    this.trackManager.addElement(newElement, track, TimelineMode.RIPPLE);

    if (template && this.extraElementData.isLayoutElement(template)) {
      const { layoutType } =
        this.extraElementData.findLayoutRelatedElements(template);
      this.extraElementDataSnapshot = this.extraElementData.cloneValue();
      if (layoutType) {
        const command = new AddLayoutElementsCommand(this.editor, newElement, {
          layoutType: layoutType as 'stacked-1-1' | 'stacked-1-3',
        });
        this.editor.executeCommand(command);
      }
    }

    // Re-calculate timeline duration and mark dirty so observers fire
    this.editor.timeline.recalculateDuration();
    this.editor.markDirty('transcript_simple_insert');

    return true;
  }

  undo(): boolean {
    const track = this.getTranscriptTrack();
    if (!track) return false;

    // Remove all current elements
    [...track.elements].forEach(el => {
      this.trackManager.removeElement(el, TimelineMode.DEFAULT);
    });

    // Restore saved elements
    for (const saved of this.originalTrackState) {
      const restored = VideoElement.fromJSON(saved.state);
      // Restore original transcript on the element if present
      if ((saved.state as any).transcriptSegment) {
        restored.setOriginalTranscription(
          Array.from(
            this.editor.legacyTranscript?.getOriginalTranscription() || [],
          ),
        );
      }
      this.trackManager.addElement(restored, track, TimelineMode.DEFAULT);
    }

    if (this.extraElementDataSnapshot) {
      this.extraElementData.restoreValue(this.extraElementDataSnapshot);
    }

    this.editor.timeline.recalculateDuration();
    this.editor.markDirty('transcript_simple_insert_undo');

    return true;
  }

  getDescription(): string {
    return `Simple insert words (${this.startInitialIdx}-${this.endInitialIdx}) at ${this.insertTime}s`;
  }

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------

  private getTranscriptTrack(): Track | null {
    if (this.transcriptTrack) return this.transcriptTrack;
    const track = this.editor.getTranscriptTrack();
    if (!track) return null;
    this.transcriptTrack = track;
    return track;
  }

  private collectWords(): TranscriptTextElement[] {
    if (!this.editor.legacyTranscript) return [];
    const original = this.editor.legacyTranscript.getOriginalTranscription();
    const results: TranscriptTextElement[] = [];
    for (let i = this.startInitialIdx; i <= this.endInitialIdx; i++) {
      const el = original[i];
      if (el && el.type === 'text') {
        results.push(el as TranscriptTextElement);
      }
    }
    return results.sort((a, b) => a.initial_index - b.initial_index);
  }
}
